import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Download,
  Inbox,
  MoreHorizontal,
  Paperclip,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { StatusPill } from '@/components/status-dot';
import {
  exportUrl,
  useDeleteSubmission,
  usePurgeSubmissions,
  useRestoreSubmission,
  useSubmissions,
  useUpdateSubmissionStatus,
  type SubmissionFilters,
} from '@/lib/form-submissions';
import { isAdminSurface } from '@/lib/surface';
import { useForms, useFormAnalytics, useMyForms } from '@/lib/forms';
import { useSites } from '@/lib/sites';
import { Switch } from '@/components/switch';
import { SubmissionChart } from '@/components/submission-chart';
import { StatDelta } from '@/components/stat-delta';
import { Card, CardBody, CardHeader } from '@/components/card';
import { formatDateTime } from '@/lib/format';
import type { FormSubmissionData, FormSubmissionValueData } from '@/types/api';
import { SubmissionDialog } from './submission-dialog';

const STATUS_TONE = { new: 'info', read: 'neutral', archived: 'neutral' } as const;

export function FormSubmissionsIndex() {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);

  // Filters live in the URL so a filtered inbox is linkable and survives a
  // refresh. Any change resets to page 1 — filtering from page 4 otherwise
  // lands you on an empty list.
  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // Narrowing the website invalidates whichever form was picked under it.
    if (key === 'site_id') next.delete('client_form_id');
    setParams(next);
    setPage(1);
  }

  const siteId = Number(params.get('site_id')) || undefined;
  const formId = Number(params.get('client_form_id')) || undefined;
  const status = params.get('status') ?? '';
  const q = params.get('q') ?? '';
  const flagged = params.get('flagged') === '1';
  // Admin only: the portal has no delete, so it has nothing to show here.
  const deleted = isAdminSurface && params.get('deleted') === '1';

  const filters: SubmissionFilters = {
    site_id: siteId,
    client_form_id: formId,
    status: status || undefined,
    flagged: flagged || undefined,
    q: q || undefined,
    deleted: deleted || undefined,
    page,
  };

  const submissions = useSubmissions(filters);
  const data = submissions.data?.data;
  const meta = submissions.data?.meta;
  const hasFilters = Boolean(siteId || formId || status || q || flagged);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Inbox}
        title="Form submissions"
        subtitle={
          isAdminSurface
            ? 'Everything your clients’ website forms have collected.'
            : 'Enquiries your website forms have collected.'
        }
        action={
          data && data.length > 0 ? (
            <div className="flex items-center gap-2">
              {deleted ? <PurgeButton filters={filters} total={meta?.total ?? data.length} /> : null}
              <a href={exportUrl(filters)}>
                <Button variant="secondary">
                  <Download className="size-4" /> Export CSV
                </Button>
              </a>
            </div>
          ) : null
        }
      />

      {!isAdminSurface ? <PortalInsights formId={formId} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Search name, email or subject…"
          value={q}
          onChange={(e) => setFilter('q', e.target.value)}
          className="max-w-xs"
        />

        <FormScopePickers siteId={siteId} formId={formId} onChange={setFilter} />

        <Select
          value={status}
          onChange={(e) => setFilter('status', e.target.value)}
          className="max-w-[150px]"
          aria-label="Status"
        >
          <option value="">All statuses</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="archived">Archived</option>
        </Select>

        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={flagged}
            onChange={(next) => setFilter('flagged', next ? '1' : '')}
            label="Needs checking"
          />
          <span className="text-[var(--color-foreground-muted)]">Needs checking</span>
        </label>

        {isAdminSurface ? (
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={deleted}
              onChange={(next) => setFilter('deleted', next ? '1' : '')}
              label="Show deleted"
            />
            <span className="text-[var(--color-foreground-muted)]">Show deleted</span>
          </label>
        ) : null}

        {hasFilters ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setParams(new URLSearchParams());
              setPage(1);
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {submissions.isLoading ? (
        <SkeletonList rows={6} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title={
            deleted
              ? 'Nothing deleted'
              : hasFilters
                ? 'Nothing matches those filters'
                : 'No submissions yet'
          }
          description={
            deleted
              ? 'Submissions you delete wait here until you empty them for good.'
              : hasFilters
                ? 'Try a different search, or clear the filters.'
                : isAdminSurface
                  ? 'Once a client site is posting to its form endpoint, submissions land here.'
                  : 'Enquiries from your website forms will appear here.'
          }
        />
      ) : (
        <div className="space-y-4">
          <SubmissionTable
            submissions={data}
            onOpen={setOpenId}
            showFormColumn={!formId}
            deleted={deleted}
          />
          {meta && meta.last_page > 1 ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}

      {openId ? <SubmissionDialog id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

/** How many mapped answers get their own column before the rest are dropped. */
const MAX_ANSWER_COLUMNS = 4;

/**
 * Roles the table already has a column for: the name roles compose into From,
 * and the message has its own column. Toggling those on is reasonable — you do
 * want them in the list — so honour the intent and drop the duplicate rather
 * than the toggle. Left in, they also ate the four-column budget, which is what
 * pushed the genuinely extra answers off the end.
 */
const ROLES_ALREADY_COLUMNS = new Set(['name', 'first_name', 'last_name', 'message']);

/** A submission's summary answers, minus the ones another column already shows. */
function listableAnswers(submission: FormSubmissionData): FormSubmissionValueData[] {
  return submission.summary.filter((v) => !v.role || !ROLES_ALREADY_COLUMNS.has(v.role));
}

function SubmissionTable({
  submissions,
  onOpen,
  showFormColumn,
  deleted,
}: {
  submissions: FormSubmissionData[];
  onOpen: (id: number) => void;
  showFormColumn: boolean;
  deleted: boolean;
}) {
  // The columns are whatever the field maps flag with "show in the list" — that
  // is the entire job of that toggle. Derived from the rows on screen, so a
  // cross-form list simply shows the union of what its forms chose.
  const answerColumns: string[] = [];
  for (const submission of submissions) {
    for (const value of listableAnswers(submission)) {
      if (!answerColumns.includes(value.label) && answerColumns.length < MAX_ANSWER_COLUMNS) {
        answerColumns.push(value.label);
      }
    }
  }

  // The message itself never gets a summary toggle — it is too long for a cell
  // and every form has one — so it earns a column of its own whenever any row
  // on screen actually carries one.
  const showMessage = submissions.some((s) => s.subject || s.preview);

  return (
    <div className="overflow-x-auto rounded-xl border border-border/70 bg-card shadow-xs">
      <table className="w-full text-sm">
        <thead className="border-b border-border/60 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
          <tr>
            <th className="px-4 py-2.5 text-left font-semibold">From</th>
            {answerColumns.map((label) => (
              <th key={label} className="px-4 py-2.5 text-left font-semibold">
                {label}
              </th>
            ))}
            {showMessage ? (
              <th className="px-4 py-2.5 text-left font-semibold">Message</th>
            ) : null}
            {showFormColumn ? (
              <th className="px-4 py-2.5 text-left font-semibold">Form</th>
            ) : null}
            <th className="px-4 py-2.5 text-left font-semibold">Received</th>
            <th className="px-4 py-2.5 text-left font-semibold">Status</th>
            <th className="w-10 px-2 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {submissions.map((submission) => (
            <SubmissionRow
              key={submission.id}
              submission={submission}
              answerColumns={answerColumns}
              showMessage={showMessage}
              showFormColumn={showFormColumn}
              deleted={deleted}
              onOpen={() => onOpen(submission.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubmissionRow({
  submission,
  answerColumns,
  showMessage,
  showFormColumn,
  deleted,
  onOpen,
}: {
  submission: FormSubmissionData;
  answerColumns: string[];
  showMessage: boolean;
  showFormColumn: boolean;
  deleted: boolean;
  onOpen: () => void;
}) {
  const setStatus = useUpdateSubmissionStatus();
  const remove = useDeleteSubmission();
  const restore = useRestoreSubmission();
  const confirm = useConfirm();
  const toast = useToast();

  const answers = listableAnswers(submission);
  const answerFor = (label: string) => answers.find((v) => v.label === label);

  return (
    <tr onClick={onOpen} className="cursor-pointer transition-colors hover:bg-muted/40">
      <td className="max-w-[220px] px-4 py-2.5">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium text-foreground">
            {submission.name || submission.email || 'Anonymous'}
          </span>
          {!submission.is_valid ? (
            <AlertTriangle
              className="size-3.5 shrink-0 text-[var(--warning)]"
              aria-label="Some values did not match the field map"
            />
          ) : null}
          {submission.attachments.length > 0 ? (
            <span className="inline-flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
              <Paperclip className="size-3" />
              {submission.attachments.length}
            </span>
          ) : null}
        </div>
      </td>

      {answerColumns.map((label) => (
        <td
          key={label}
          className="max-w-[260px] truncate px-4 py-2.5 text-[var(--color-foreground-muted)]"
          title={renderCell(answerFor(label)?.value)}
        >
          {renderCell(answerFor(label)?.value)}
        </td>
      ))}

      {showMessage ? (
        <td
          className="max-w-[320px] truncate px-4 py-2.5 text-[var(--color-foreground-muted)]"
          title={submission.subject ?? submission.preview ?? undefined}
        >
          {submission.subject ?? submission.preview ?? '—'}
        </td>
      ) : null}

      {showFormColumn ? (
        <td className="max-w-[200px] truncate px-4 py-2.5 text-xs text-muted-foreground">
          {isAdminSurface && submission.client_name ? (
            <Link
              to={`/clients/${submission.client_id}`}
              onClick={(e) => e.stopPropagation()}
              className="hover:underline"
            >
              {submission.client_name}
            </Link>
          ) : null}
          {isAdminSurface && submission.client_name && submission.form_name ? ' · ' : null}
          {submission.form_name}
        </td>
      ) : null}

      <td className="whitespace-nowrap px-4 py-2.5 text-xs text-muted-foreground">
        {formatDateTime(submission.submitted_at)}
      </td>

      <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <StatusPill tone={STATUS_TONE[submission.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
            {submission.status}
          </StatusPill>
          <Select
            value={submission.status}
            onChange={(e) => setStatus.mutate({ id: submission.id, status: e.target.value })}
            className="h-8 w-[110px] text-xs"
            aria-label="Change status"
          >
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="archived">Archived</option>
          </Select>
        </div>
      </td>

      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
        {isAdminSurface ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground aria-expanded:bg-muted"
              aria-label={`Actions for ${submission.name || submission.email || 'this submission'}`}
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={onOpen}>Open</DropdownMenuItem>
              {deleted ? (
                <DropdownMenuItem
                  onClick={async () => {
                    await restore.mutateAsync(submission.id);
                    toast.success('Submission restored');
                  }}
                >
                  <RotateCcw className="size-4" /> Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={async () => {
                    const ok = await confirm({
                      title: 'Delete this submission?',
                      body: 'It moves to the deleted list, files and all, where you can restore it or empty it for good. Set it to Archived instead if you only want it out of the way.',
                      confirmLabel: 'Delete',
                      tone: 'danger',
                    });
                    if (ok) {
                      await remove.mutateAsync(submission.id);
                      toast.success('Submission deleted');
                    }
                  }}
                >
                  <Trash2 className="size-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Empties the deleted list. Deliberately scoped to the filters on screen rather
 * than to everything ever deleted, the same way the CSV export is: a button that
 * reaches past what you can see is a button nobody can trust.
 */
function PurgeButton({ filters, total }: { filters: SubmissionFilters; total: number }) {
  const purge = usePurgeSubmissions();
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <Button
      variant="danger"
      disabled={purge.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: `Permanently delete ${total} submission${total === 1 ? '' : 's'}?`,
          body: 'This cannot be undone. The submissions and every file attached to them are erased from storage for good.',
          confirmLabel: 'Delete for good',
          tone: 'danger',
        });
        if (!ok) return;
        const purged = await purge.mutateAsync(filters);
        toast.success(`${purged} submission${purged === 1 ? '' : 's'} deleted for good`);
      }}
    >
      <Trash2 className="size-4" /> Empty deleted
    </Button>
  );
}

function renderCell(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Website then form, cascading — one site can host several forms, so picking a
 * site narrows the form list to that site's.
 *
 * Admin reads the full site list; the portal has no access to /v1/sites, so it
 * derives its options from the forms it is allowed to see and skips the website
 * picker when there is only one site to choose from.
 */
function FormScopePickers({
  siteId,
  formId,
  onChange,
}: {
  siteId: number | undefined;
  formId: number | undefined;
  onChange: (key: string, value: string) => void;
}) {
  const sites = useSites({ status: 'active' });
  const adminForms = useForms(siteId ? { site_id: siteId } : {});
  const portalForms = useMyForms();

  const formList = (isAdminSurface ? adminForms.data?.data : portalForms.data?.data) ?? [];
  const scopedForms = siteId ? formList.filter((f) => f.site_id === siteId) : formList;

  // On the portal, the sites are whatever the visible forms belong to.
  const portalSites = [
    ...new Map(
      formList
        .filter((f) => f.site_name)
        .map((f) => [f.site_id, { id: f.site_id, name: f.site_name!, client_name: null }]),
    ).values(),
  ];
  const siteList = isAdminSurface ? (sites.data?.data ?? []) : portalSites;

  return (
    <>
      {siteList.length > 0 ? (
        <Select
          value={siteId ?? ''}
          onChange={(e) => onChange('site_id', e.target.value)}
          className="max-w-[220px]"
          aria-label="Website"
        >
          <option value="">All websites</option>
          {siteList.map((s) => (
            <option key={s.id} value={s.id}>
              {isAdminSurface && s.client_name ? `${s.client_name} — ${s.name}` : s.name}
            </option>
          ))}
        </Select>
      ) : null}

      {scopedForms.length > 0 ? (
        <Select
          value={formId ?? ''}
          onChange={(e) => onChange('client_form_id', e.target.value)}
          className="max-w-[200px]"
          aria-label="Form"
        >
          <option value="">All forms</option>
          {scopedForms.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </Select>
      ) : null}
    </>
  );
}

/**
 * Portal-only headline: this month against last, plus the trend. Admins get the
 * same numbers with more depth on the form's own Insights tab.
 */
function PortalInsights({ formId }: { formId: number | undefined }) {
  const forms = useMyForms();
  const list = forms.data?.data ?? [];
  const selected = formId ?? list[0]?.id;
  const analytics = useFormAnalytics(selected, { granularity: 'month' });
  const data = analytics.data;

  if (!data || data.all_time_count === 0) return null;

  const form = list.find((f) => f.id === selected);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatDelta title={data.current_bucket.label} comparison={data.current_bucket} />
        <StatDelta title={data.period_total.label} comparison={data.period_total} />
      </div>
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">
            {form ? `${form.name} — submissions over time` : 'Submissions over time'}
          </h3>
        </CardHeader>
        <CardBody>
          <SubmissionChart buckets={data.buckets} height={150} />
        </CardBody>
      </Card>
    </div>
  );
}
